import { useState, useEffect } from "react";
import BaseModal from "../../../components/BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import { useToast } from "../../../context/ToastContext";
import styles from "./AddApiSettingModal.module.css";

const emptyForm = {
  name: "",
  type: "utility",
  baseUrl: "",
  authType: "api_key",
  environment: "test",
  active: true,
  description: "",
  apiKey: "",
  username: "",
  password: "",
  bearerToken: "",
  clientId: "",
  clientSecret: "",
  tokenUrl: "",
  customHeaders: "",
  timeoutMs: 30000,
  retryCount: 3,
};

const toForm = (data) => {
  if (!data) return emptyForm;
  return {
    name: data.name || "",
    type: data.type || "utility",
    baseUrl: data.baseUrl || "",
    authType: data.authType || "api_key",
    environment: data.environment || "test",
    active: data.active ?? true,
    description: data.description || "",
    apiKey: data.apiKey || "",
    username: data.username || "",
    password: data.password || "",
    bearerToken: data.bearerToken || "",
    clientId: data.clientId || "",
    clientSecret: data.clientSecret || "",
    tokenUrl: data.tokenUrl || "",
    customHeaders: data.customHeaders || "",
    timeoutMs: data.timeoutMs ?? 30000,
    retryCount: data.retryCount ?? 3,
  };
};

const AddApiSettingModal = ({ open, onClose, mode = "add", initialData, onSave, saveLoading = false }) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initialData) {
      setForm(toForm(initialData));
    } else if (mode === "add") {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      showToast("API name is required", "error");
      return;
    }
    if (!form.baseUrl.trim()) {
      showToast("Base URL is required", "error");
      return;
    }

    const payload = {
      name: form.name.trim(),
      type: form.type,
      baseUrl: form.baseUrl.trim(),
      authType: form.authType,
      environment: form.environment,
      active: form.active,
      description: form.description.trim(),
      timeoutMs: Number(form.timeoutMs) || 30000,
      retryCount: Number(form.retryCount) || 3,
      customHeaders: form.customHeaders.trim() || undefined,
    };

    if (form.authType === "api_key") {
      payload.apiKey = form.apiKey;
    } else if (form.authType === "basic") {
      payload.username = form.username;
      payload.password = form.password;
    } else if (form.authType === "bearer") {
      payload.bearerToken = form.bearerToken;
    } else if (form.authType === "oauth2") {
      payload.clientId = form.clientId;
      payload.clientSecret = form.clientSecret;
      payload.tokenUrl = form.tokenUrl;
    }

    if (onSave) {
      onSave(payload);
    } else {
      onClose();
    }
  };

  const renderAuthFields = () => {
    switch (form.authType) {
      case "api_key":
        return (
          <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <label>API Key</label>
            <input
              type="password"
              placeholder="Enter API key"
              value={form.apiKey}
              onChange={(e) => handleChange("apiKey", e.target.value)}
            />
          </div>
        );
      case "basic":
        return (
          <>
            <div className={styles.field}>
              <label>Username</label>
              <input
                placeholder="Username"
                value={form.username}
                onChange={(e) => handleChange("username", e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label>Password</label>
              <input
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => handleChange("password", e.target.value)}
              />
            </div>
          </>
        );
      case "bearer":
        return (
          <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
            <label>Bearer Token</label>
            <input
              type="password"
              placeholder="Enter bearer token"
              value={form.bearerToken}
              onChange={(e) => handleChange("bearerToken", e.target.value)}
            />
          </div>
        );
      case "oauth2":
        return (
          <>
            <div className={styles.field}>
              <label>Client ID</label>
              <input
                placeholder="Client ID"
                value={form.clientId}
                onChange={(e) => handleChange("clientId", e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label>Client Secret</label>
              <input
                type="password"
                placeholder="Client Secret"
                value={form.clientSecret}
                onChange={(e) => handleChange("clientSecret", e.target.value)}
              />
            </div>
            <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
              <label>Token URL</label>
              <input
                placeholder="https://auth.example.com/token"
                value={form.tokenUrl}
                onChange={(e) => handleChange("tokenUrl", e.target.value)}
              />
            </div>
          </>
        );
      case "none":
      default:
        return null;
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "edit" ? "Edit API Setting" : "Add New API Setting"}
      width="720px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saveLoading}>
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : mode === "edit" ? (
              "Save Changes"
            ) : (
              "+ Create API Setting"
            )}
          </button>
        </>
      }
    >
      <div className={styles.sectionLabel}>General Information</div>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>API Name</label>
          <input
            placeholder="e.g. Weather API"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>API Type</label>
          <select value={form.type} onChange={(e) => handleChange("type", e.target.value)}>
            <option value="utility">Utility</option>
            <option value="integration">Integration</option>
            <option value="export">Export</option>
          </select>
        </div>
        <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
          <label>Base URL</label>
          <input
            placeholder="https://api.example.com/v1"
            value={form.baseUrl}
            onChange={(e) => handleChange("baseUrl", e.target.value)}
          />
        </div>
        <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
          <label>Description</label>
          <textarea
            placeholder="Brief description of this API's purpose…"
            rows={2}
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
            className={styles.textarea}
          />
        </div>
      </div>

      <div className={styles.sectionLabel}>Authentication</div>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>Auth Type</label>
          <select value={form.authType} onChange={(e) => handleChange("authType", e.target.value)}>
            <option value="api_key">API Key</option>
            <option value="basic">Basic Auth</option>
            <option value="bearer">Bearer Token</option>
            <option value="oauth2">OAuth2</option>
            <option value="none">None</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>Environment</label>
          <select value={form.environment} onChange={(e) => handleChange("environment", e.target.value)}>
            <option value="test">Test</option>
            <option value="production">Production</option>
          </select>
        </div>
        {renderAuthFields()}
      </div>

      <div className={styles.sectionLabel}>Configuration</div>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>Timeout (ms)</label>
          <input
            type="number"
            placeholder="30000"
            value={form.timeoutMs}
            onChange={(e) => handleChange("timeoutMs", e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label>Retry Count</label>
          <input
            type="number"
            placeholder="3"
            min={0}
            max={10}
            value={form.retryCount}
            onChange={(e) => handleChange("retryCount", e.target.value)}
          />
        </div>
        <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
          <label>Custom Headers (JSON)</label>
          <textarea
            placeholder='{"X-Custom-Header": "value"}'
            rows={2}
            value={form.customHeaders}
            onChange={(e) => handleChange("customHeaders", e.target.value)}
            className={styles.textarea}
          />
        </div>
        <div className={styles.toggleRow}>
          <div className={styles.field}>
            <label>Status</label>
            <ToggleSwitch
              checked={form.active}
              onChange={() => handleChange("active", !form.active)}
            />
          </div>
        </div>
      </div>

      {mode === "edit" && (
        <div className={styles.infoBox}>
          🔒 Secrets are encrypted at rest. Changing credentials will take effect immediately.
        </div>
      )}
    </BaseModal>
  );
};

export default AddApiSettingModal;